/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useRef } from 'react';

import { useSubjectDocumentsRefreshMutation } from '@/services/api/documentSubjectService';
import { useRefreshDocumentMutation } from '@/services/api/lettersService';

/** Max consecutive errors before polling backs off completely. */
const MAX_CONSECUTIVE_ERRORS = 3;

/**
 * useDocumentRefresh – polls the backend to refresh the status of one or more
 * DocumentInstances.
 *
 * @param matterId    – the parent Matter ID
 * @param documentIds – array of DocumentInstance IDs to refresh
 * @param intervalMs  – polling interval (default 30 000 ms)
 * @param enabled     – set to false to pause polling (e.g. terminal document state)
 */
export const useDocumentRefresh = (
  matterId: string | null | undefined,
  documentIds: string[] | undefined,
  intervalMs = 30000,
  enabled = true,
) => {
  const [refreshDocument] = useRefreshDocumentMutation();
  const errorCount = useRef(0);
  const isRefreshing = useRef(false);

  const tick = useCallback(async () => {
    if (!matterId || !documentIds?.length) return;
    if (errorCount.current >= MAX_CONSECUTIVE_ERRORS) return;
    if (isRefreshing.current) return;

    isRefreshing.current = true;
    try {
      const results = await Promise.allSettled(
        documentIds.map((id) =>
          refreshDocument({ matterId, documentInstanceId: id }).unwrap(),
        ),
      );

      if (results.every((r) => r.status === 'rejected')) {
        errorCount.current += 1;
      } else {
        errorCount.current = 0;
      }
    } finally {
      isRefreshing.current = false;
    }
  }, [matterId, documentIds?.toString(), refreshDocument]);

  useEffect(() => {
    if (!enabled) return undefined;
    if (!matterId || !documentIds || documentIds.length === 0) return undefined;

    errorCount.current = 0;
    tick(); // immediate
    const handle = setInterval(tick, intervalMs);
    return () => clearInterval(handle);
  }, [matterId, documentIds?.toString(), intervalMs, tick, enabled]);
};

/**
 * useSubjectDocumentsRefresh – polls the refresh endpoint for a single
 * DocumentInstance scoped to a subject (intake or matter).
 *
 * @param subjectId   – the intake or matter ID
 * @param subjectType – 'intake' or 'matter'
 * @param documentId  – the DocumentInstance ID
 * @param intervalMs  – polling interval (default 30 000 ms)
 * @param enabled     – set to false to pause polling (e.g. terminal document state)
 */
export const useSubjectDocumentsRefresh = (
  subjectId: string | null | undefined,
  subjectType: 'intake' | 'matter',
  documentId: string | null | undefined,
  intervalMs = 30000,
  enabled = true,
) => {
  const [refreshDocument] = useSubjectDocumentsRefreshMutation();
  const errorCount = useRef(0);

  const tick = useCallback(() => {
    if (!subjectId) return;
    if (!documentId) return;
    if (errorCount.current >= MAX_CONSECUTIVE_ERRORS) return;
    refreshDocument({
      subjectType,
      subjectId,
      id: documentId,
      // Backend requires body but doesn't use it for refresh action
      patchedDocumentInstance: {},
    })
      .unwrap()
      .then(() => {
        errorCount.current = 0;
      })
      .catch(() => {
        errorCount.current += 1;
      });
  }, [subjectId, subjectType, documentId]);

  useEffect(() => {
    if (!enabled) return undefined;
    if (!subjectId || !documentId) return undefined;

    errorCount.current = 0;
    tick(); // immediate
    const handle = setInterval(tick, intervalMs);
    return () => clearInterval(handle);
  }, [subjectId, subjectType, documentId, intervalMs, tick, enabled]);
};
